<!--*
 * Component to show place information
 *
 * @author   mauricio.figueroa
 * @date     2020-07-03
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div class="card pt-5 pl-5 pr-5 pb-7 shadow-card">
    <div class="row">
      <div class="col--12 col--md--6">
        <div class="row m-0">
          <div class="h3 title">
            {{ siteName }}
          </div>
        </div>
        <div class="row m-0">
          <div class="h5 gray text-break">
            {{ `${$t('siteDetails.detail.siteCode.label')}: ${siteCode}` }}
          </div>
        </div>
        <div class="row before-image">
          <div class="text-right">
            <smtt-icon
              class=" align-middle mr-2"
              icon="info-circle"
              color="#6040b9"
            />
            {{ surveysStatus }}
          </div>
        </div>
        <div v-if="!hasLocationConfigDisabled">
          <div v-if="checkingPermission" class="d-none d--md-block text-center">
              <status-label variation="info">
                {{ $t('siteDetails.detail.checkingPermission') }}
              </status-label>
          </div>
          <div v-else-if="!hasLocationPermission" class="d-none d--md-block text-center">
              <status-label variation="danger">
                {{ $t('siteDetails.detail.missingLocationPermission') }}
              </status-label>
          </div>
<!--          <div v-else-if="siteDirections && siteLocationNotFound" class="row m-0 my-4">-->
<!--            <status-label variation="danger">-->
<!--              {{ $t('siteDetails.detail.directionsNotFound') }}-->
<!--            </status-label>-->
<!--          </div>-->
          <!-- <app-button
              v-else-if="siteDirections && !siteLocationNotFound"
              class="my-2 d-none d--md-block"
              full-width="true"
              theme="primary"
              btn-type="outline"
              size="lg"
              @click="onGetDirection()"
          >
            {{ $t('siteDetails.details.buttonGetDirection') }}
          </app-button> -->
        </div>
      </div>
      <div class="col--12 col--md--6">
        <div class="row m-0">
          <div v-if="siteDirections" class="text-justify">
            <b class="text-16">{{ siteDirections }}</b>
          </div>
          <div v-else>
            {{ $t('siteDetails.detail.noDirection') }}
          </div>
        </div>
        <div class="row m-0 mt-3">
          <div v-if="sitePhone">
            {{ $t('siteDetails.alreadyThere.details.phone') }}
            <u class="text-purple text-16">{{ sitePhone }}</u>
          </div>
        </div>
        <div v-if="siteOpeningHours" class="row m-0 text-16 mt-3">
          {{ siteOpeningHours }}
        </div>
        <div class="row m-0 mt-3">
          <div v-if="siteContactName">
            {{ $t('siteDetails.alreadyThere.details.contactName') }}
            <u class="text-16">{{ siteContactName }}</u>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!hasLocationConfigDisabled">
      <div v-if="checkingPermission" class="text-center my-4 d-block d--md-none">
          <status-label variation="info">
                {{ $t('siteDetails.detail.checkingPermission') }}
          </status-label>
      </div>
      <div v-else-if="!hasLocationPermission" class="text-center my-4 d-block d--md-none">
          <status-label variation="danger">
                {{ $t('siteDetails.detail.missingLocationPermission') }}
          </status-label>
      </div>
      <!-- <app-button
        v-else-if="siteDirections && !siteLocationNotFound"
        class="my-7 d-block d--md-none"
        full-width="true"
        theme="primary"
        btn-type="outline"
        size="lg"
        @click="onGetDirection()"
      >
        {{ $t('siteDetails.details.buttonGetDirection') }}
      </app-button> -->
    </div>
    <SiteProgress
      class="p-0 mt-3"
      :total="rooms.total"
      :completed="rooms.completed"
      :in-revision="rooms.inRevision"
      :in-progress="rooms.inProgress"
      :pending="rooms.pending"
      :returned="rooms.returned"
      title="siteProgress.counter.surveys"
      :has-workflow="hasWorkflow"
    />
    <div v-if="phaseName === 'Pending' && deadline" class="col text-right font-weight-bold">
      <smtt-separator />
      {{
        $t('siteDetails.details.phase.deadline.label',
        { 'deadline': new Date(deadline).toLocaleDateString(this.$store.state.currentLocale) })
      }}
    </div>
  </div>
</template>

<script>
import SiteProgress from '../SharedComponents/SiteProgress';

import store from '@/store/store';
import StatusLabel from "@/components/StatusLabel.vue";
import { mapActions } from 'vuex';
import AppButton from '@/components/AppButton';

export default {
  'name': 'Details',
  'props': [
    'siteName',
    'sitePlacesSurvey',
    'siteDirections',
    'sitePhone',
    'siteCode',
    'openHour',
    'closeHour',
    'image',
    'siteRooms',
    'latitude',
    'longitude',
    'siteFullAddress',
    'siteContactName',
    'phaseName',
    'transitionComment',
    'deadline',
    'siteLocationNotFound',
    'hasWorkflow'
  ],
  'components': { StatusLabel, SiteProgress, AppButton },
  data: function () {
    return {
      hasLocationPermission: true,
      hasLocationConfigDisabled: false,
      checkingPermission: true,
    }
  },
  'methods': {
    ...mapActions([
      'requestMyLocation',
    ]),
    onGetDirection() {
      let directionData = {};
      if (this.latitude !== null && this.longitude !== null) {
        directionData = { 'lat': this.latitude, 'lng': this.longitude }
      } else {
        directionData = { 'address': this.siteFullAddress }
      }

      store.commit('setSiteDetailsDirection', { 'direction': directionData });

      if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        // Is a mobile device, try to open map application
        if (directionData.lat !== undefined && directionData.lng !== undefined) {
          window.location.href = `geo:${directionData.lat},${directionData.lng}`;
        } else if (directionData.address !== undefined) {
          window.location.href = `geo:0,0?q=${directionData.address.split(' ').join('+')}`;
        } else {
          window.location.href = 'geo:0,0';
        }
      } else {
        this.$router.push({ 'name': 'MapNavigation' });
      }


    },
  },
  'computed': {
    rooms() {
      return this.siteRooms !== undefined ? this.siteRooms : { 'total': 0, 'completed': 0, 'inProgress': 0, 'pending': 0 }
    },
    surveysStatus() {
      if (this.siteRooms !== undefined) {
        if (this.siteRooms.pending > 0) {
          return `${this.siteRooms.pending} ${this.siteRooms.pending > 1 ? this.$t('siteDetails.detail.surveyStatus.2.plural') : this.$t('siteDetails.detail.surveyStatus.2.singular') }`;
        } else {
          return this.$t('siteDetails.detail.surveyStatus.completed');
        }
      } else {
        return '...';
      }
    },
    siteOpeningHours() {
      if (this.openHour && this.closeHour) {
        let openState = '';
        const currentDate = new Date();

        const dateOpen = getDateFormat(this.openHour);
        const dateClose = getDateFormat(this.closeHour);

        if (currentDate > dateOpen && currentDate < dateClose) {
          openState = this.$t('siteDetails.detail.openStatusOpen');
        } else {
          openState = this.$t('siteDetails.detail.openStatusClose');
        }
        return this.$t('siteDetails.detail.time', { 'start': this.openHour, 'end': this.closeHour, 'status': openState })
      }
    },
    async checkLocationPermission() {
      try {
        this.checkingPermission = true;
        await this.requestMyLocation();
      } catch(error) {
        if (error === 'Location disabled by config') {
          this.hasLocationConfigDisabled = true;
        }
        if (error === "Couldn't get current position") {
          this.hasLocationPermission = false;
        }
        if (error.code === 1) {
          this.hasLocationPermission = false;
        }
      } finally {
        this.checkingPermission = false;
      }
    console.log('hasLocationConfigDisabled', this.hasLocationConfigDisabled, 'hasLocationPermission', this.hasLocationPermission);
    },
  },
  created() {
    this.checkLocationPermission;
  }
}

// Helper functions
const getDateFormat = (stringHour) => {
  const result = new Date();
  result.setHours(stringHour.split(':')[0]);
  result.setMinutes(stringHour.split(':')[1]);
  result.setSeconds(0);

  return result;
}
</script>

<style scoped>
  .title {
    margin: 0 0 4px 0;
  }
  .text-16 {
    font-size: 16px;
  }
  .before-image {
    margin: 0 0 10px 0;
  }
</style>
